import * as React from 'react';
import {
  BgColorsOutlined,
  BoldOutlined,
  ClearOutlined,
  FontColorsOutlined,
  ItalicOutlined,
  LinkOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  OrderedListOutlined,
  PictureOutlined,
  RedoOutlined,
  StrikethroughOutlined,
  TableOutlined,
  UndoOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { MenuProps } from 'antd';
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_ELEMENT_COMMAND,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import type { ElementFormatType } from 'lexical';
import { useTranslation } from 'react-i18next';
import { useEffectivePrototypeEnabled } from '../../../Prototype';
import styles from '../../lexical-editor.module.css';
import type { BlockType } from '../../utils';
import {
  ALIGNMENT_OPTIONS,
  BACKGROUND_COLOR_PRESETS,
  BLOCK_OPTIONS,
  DEFAULT_BACKGROUND_COLOR,
  DEFAULT_TEXT_COLOR,
  FONT_SIZE_OPTIONS,
  TEXT_COLOR_PRESETS,
} from './constants';
import {
  ToolbarButton,
  ToolbarColorButton,
  ToolbarDropdownButton,
} from './controls';
import { ImageEditorModal, LinkEditorModal, TableEditorModal } from './modals';
import { useToolbarFormatting } from './useToolbarFormatting';
import { useToolbarHistory } from './useToolbarHistory';
import { useToolbarImage } from './useToolbarImage';
import { useToolbarLink } from './useToolbarLink';
import { useToolbarTable } from './useToolbarTable';
import { useToolbarVideo } from './useToolbarVideo';
import { VideoEditorModal } from './VideoEditorModal';

interface ToolbarPluginProps {
  disabled?: boolean;
  /** Show only the essentials; fold the rest into a single "More" menu. */
  compact?: boolean;
  /** Opt in to the prototype Insert → Video item. */
  enableVideo?: boolean;
  /** Insertable merge variables shown in a "Variable" dropdown. */
  variables?: { label: string; token: string }[];
}

export function ToolbarPlugin({
  disabled = false,
  compact = false,
  enableVideo = false,
  variables,
}: ToolbarPluginProps) {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation();
  const [isLinkEditorVisible, setIsLinkEditorVisible] = React.useState(false);
  const history = useToolbarHistory(editor);
  const { formatting, selection } = useToolbarFormatting(
    editor,
    isLinkEditorVisible
  );
  const link = useToolbarLink(editor, selection);
  const image = useToolbarImage(editor);
  const table = useToolbarTable(editor);
  const video = useToolbarVideo(editor);
  const prototypeEnabled = useEffectivePrototypeEnabled();
  const isVideoEnabled = enableVideo && prototypeEnabled;

  React.useEffect(() => {
    setIsLinkEditorVisible(link.isEditorVisible);
  }, [link.isEditorVisible]);

  // Lists have their own buttons, so the style menu only covers block types.
  const blockLabel =
    BLOCK_OPTIONS.find((option) => option.value === formatting.blockType)
      ?.label ?? 'Normal';

  const blockMenuItems: MenuProps['items'] = BLOCK_OPTIONS.map((option) => ({
    key: option.value,
    label: option.label,
  }));

  const fontSizeItems: MenuProps['items'] = FONT_SIZE_OPTIONS.map((option) => ({
    key: option,
    label: `${option}px`,
  }));

  const alignmentItems: MenuProps['items'] = [
    ...ALIGNMENT_OPTIONS.map((option) => ({
      key: option.value,
      label: option.label,
      icon: option.icon,
    })),
    { type: 'divider' as const },
    { key: 'outdent', label: 'Outdent', icon: <MenuFoldOutlined /> },
    { key: 'indent', label: 'Indent', icon: <MenuUnfoldOutlined /> },
  ];

  const insertItems: MenuProps['items'] = [
    { key: 'image', label: 'Image', icon: <PictureOutlined /> },
    { key: 'table', label: 'Table', icon: <TableOutlined /> },
    // PROTOTYPE: experience description only — opt in via `enableVideo`.
    ...(isVideoEnabled
      ? [
          {
            key: 'video',
            label: (
              <span data-testid="insertVideoMenuItem">
                {t('exp_video_insert_label')}
              </span>
            ),
            icon: <VideoCameraOutlined />,
          },
        ]
      : []),
  ];

  const moreItems: MenuProps['items'] = [
    {
      key: 'clear-formatting',
      label: 'Clear formatting',
      icon: <ClearOutlined />,
    },
  ];

  // Compact toolbar: everything past the text-style buttons collapses here.
  const compactMoreItems: MenuProps['items'] = [
    ...(alignmentItems ?? []),
    { type: 'divider' as const },
    { key: 'link', label: 'Link', icon: <LinkOutlined /> },
    ...(insertItems ?? []),
    { type: 'divider' as const },
    ...(moreItems ?? []),
  ];

  const handleCompactMore = (key: React.Key) => {
    if (key === 'link') return link.openEditor();
    if (key === 'image') return image.openEditor();
    if (key === 'table') return table.openEditor();
    if (key === 'video') return video.openEditor();
    if (key === 'clear-formatting') return formatting.clearFormatting();
    if (key === 'indent') {
      editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
      return;
    }
    if (key === 'outdent') {
      editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
      return;
    }
    // Remaining keys are alignment values.
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, key as ElementFormatType);
  };

  const currentAlignment =
    ALIGNMENT_OPTIONS.find(
      (option) => option.value === formatting.textAlignment
    ) ?? ALIGNMENT_OPTIONS[0];

  const hasVariables = !!variables?.length;
  const variableItems: MenuProps['items'] = variables?.map((v) => ({
    key: v.token,
    label: v.label,
  }));

  const insertVariable = (token: React.Key) => {
    editor.update(() => {
      const sel = $getSelection();
      if ($isRangeSelection(sel)) {
        sel.insertText(String(token));
      }
    });
  };

  return (
    <div
      className={styles.toolbar}
      role="toolbar"
      aria-label="Formatting toolbar"
    >
      <div className={styles.toolbarGroup}>
        <ToolbarButton
          ariaLabel="Undo"
          disabled={disabled || !history.canUndo}
          icon={<UndoOutlined />}
          onClick={() => {
            editor.dispatchCommand(UNDO_COMMAND, undefined);
          }}
        />
        <ToolbarButton
          ariaLabel="Redo"
          disabled={disabled || !history.canRedo}
          icon={<RedoOutlined />}
          onClick={() => {
            editor.dispatchCommand(REDO_COMMAND, undefined);
          }}
        />
      </div>

      <div className={styles.toolbarGroup}>
        <ToolbarDropdownButton
          active={formatting.blockType !== 'paragraph'}
          disabled={disabled}
          items={blockMenuItems}
          label={blockLabel}
          onClick={(key) => {
            formatting.applyBlockType(key as BlockType);
          }}
        />
        <ToolbarDropdownButton
          disabled={disabled}
          items={fontSizeItems}
          label={`${formatting.fontSize}px`}
          onClick={(key) => {
            formatting.applyFontSize(String(key));
          }}
        />
      </div>

      <div className={styles.toolbarGroup}>
        <ToolbarButton
          active={formatting.isBold}
          ariaLabel="Bold"
          disabled={disabled}
          icon={<BoldOutlined />}
          onClick={() => {
            formatting.applyTextStyle('bold');
          }}
        />
        <ToolbarButton
          active={formatting.isItalic}
          ariaLabel="Italic"
          disabled={disabled}
          icon={<ItalicOutlined />}
          onClick={() => {
            formatting.applyTextStyle('italic');
          }}
        />
        <ToolbarButton
          active={formatting.isUnderline}
          ariaLabel="Underline"
          disabled={disabled}
          icon={<UnderlineOutlined />}
          onClick={() => {
            formatting.applyTextStyle('underline');
          }}
        />
        <ToolbarButton
          active={formatting.isStrikethrough}
          ariaLabel="Strikethrough"
          disabled={disabled}
          icon={<StrikethroughOutlined />}
          onClick={() => {
            formatting.applyTextStyle('strikethrough');
          }}
        />
      </div>

      <div className={styles.toolbarGroup}>
        <ToolbarButton
          active={formatting.listType === 'bullet'}
          ariaLabel="Bullet list"
          disabled={disabled}
          icon={<UnorderedListOutlined />}
          onClick={() => {
            formatting.applyBlockType('bullet');
          }}
        />
        <ToolbarButton
          active={formatting.listType === 'number'}
          ariaLabel="Numbered list"
          disabled={disabled}
          icon={<OrderedListOutlined />}
          onClick={() => {
            formatting.applyBlockType('number');
          }}
        />
      </div>

      {hasVariables && (
        <div className={styles.toolbarGroup}>
          <ToolbarDropdownButton
            disabled={disabled}
            items={variableItems}
            label="Variable"
            onClick={insertVariable}
          />
        </div>
      )}

      {compact ? (
        <div className={styles.toolbarGroup}>
          <ToolbarDropdownButton
            active={false}
            disabled={disabled}
            items={compactMoreItems}
            label="More"
            onClick={handleCompactMore}
          />
        </div>
      ) : (
        <div className={styles.toolbarGroup}>
          <ToolbarColorButton
            active={formatting.textColor !== DEFAULT_TEXT_COLOR}
            ariaLabel="Text color"
            color={formatting.textColor || '#111827'}
            defaultColor={DEFAULT_TEXT_COLOR}
            disabled={disabled}
            icon={<FontColorsOutlined />}
            onChange={formatting.applyTextColor}
            presets={TEXT_COLOR_PRESETS}
          />
          <ToolbarColorButton
            active={formatting.textBackgroundColor !== DEFAULT_BACKGROUND_COLOR}
            ariaLabel="Text background color"
            color={
              formatting.textBackgroundColor === DEFAULT_BACKGROUND_COLOR
                ? '#fff1b8'
                : formatting.textBackgroundColor
            }
            defaultColor={DEFAULT_BACKGROUND_COLOR}
            disabled={disabled}
            icon={<BgColorsOutlined />}
            onChange={formatting.applyTextBackgroundColor}
            presets={BACKGROUND_COLOR_PRESETS}
          />
          <ToolbarButton
            active={!!link.url}
            ariaLabel="Insert or edit link"
            disabled={disabled}
            icon={<LinkOutlined />}
            onClick={link.openEditor}
          />
          <ToolbarDropdownButton
            active={formatting.textAlignment !== 'left'}
            disabled={disabled}
            icon={currentAlignment.icon}
            items={ALIGNMENT_OPTIONS.map((option) => ({
              key: option.value,
              label: option.label,
              icon: option.icon,
            }))}
            label={`Alignment: ${currentAlignment.label}`}
            onClick={(key) => {
              editor.dispatchCommand(
                FORMAT_ELEMENT_COMMAND,
                key as ElementFormatType
              );
            }}
          />
          <ToolbarButton
            ariaLabel="Outdent"
            disabled={disabled}
            icon={<MenuFoldOutlined />}
            onClick={() => {
              editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
            }}
          />
          <ToolbarButton
            ariaLabel="Indent"
            disabled={disabled}
            icon={<MenuUnfoldOutlined />}
            onClick={() => {
              editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
            }}
          />
          <ToolbarDropdownButton
            disabled={disabled}
            items={insertItems}
            label="Insert"
            onClick={(key) => {
              if (key === 'image') {
                image.openEditor();
                return;
              }

              if (key === 'table') {
                table.openEditor();
                return;
              }

              if (key === 'video') {
                video.openEditor();
              }
            }}
          />
          <ToolbarButton
            ariaLabel="Clear formatting"
            disabled={disabled}
            icon={<ClearOutlined />}
            onClick={formatting.clearFormatting}
          />
        </div>
      )}

      <TableEditorModal table={table} />
      <ImageEditorModal image={image} />
      <LinkEditorModal link={link} />
      {isVideoEnabled && <VideoEditorModal video={video} />}
    </div>
  );
}
